<script lang="ts">
    export default {
        name: "Vardplatsdagsoversikt",
        tabName: "Dagsöversikt",
    };
</script>

<script setup lang="ts">
    import { ref, computed, onMounted } from "vue";
    import moment from "moment";
    import "moment/dist/locale/sv";
    import SearchFilter from "@/components/SearchFilter.vue";
    import KButton from "@/components/Button.vue";
    import { printInIframe } from "@/utils/printWindow";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import { getConfig, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import { Slutenvardsbesok, VardplatsStatus, Anhorigbesok } from "./types";
    import { STATUSAR } from "./helpers";
    import VardplatsdagsoversiktList from "./VardplatsdagsoversiktList.vue";

    const tabName = "Dagsöversikt";

    const today = moment().format("YYYY-MM-DD");
    const tomorrow = moment().add(1, "days").format("YYYY-MM-DD");
    const vardplatsOrdering = "sang"; // Sortera på vårdplats
    const defaultStatus = [VardplatsStatus.BOKAD, VardplatsStatus.INSKRIVEN].join(","); // Standard status att filtrera på

    const dialog = ref<InstanceType<typeof ModalBasic> | null>(null);
    const printablecontent = ref<HTMLElement | null>(null);

    const filterValues = ref<any>({});

    const filterFields = computed(() => ({
        datum: { label: "Datum" },
        indatum: { label: "Inkommande datum" },
        sang__sal__vardavdelning: { label: "Avdelning", options: avdelningar.value },
        vardplatsbestallning__vardgivare: { label: "Vårdgivare", options: vardgivareOptions.value },
        vardplatsbestallning__operator: { label: "Operatör", options: vardpersoner.value },
        status: { label: "Status", options: STATUSAR },
    }));

    const count = ref(0);

    const items = ref<Array<Slutenvardsbesok | Anhorigbesok>>([]);

    const handleSearch = async (data?: any): Promise<void> => {
        filterValues.value = data;
        await hamtaDagsoversikt();
    };

    const avdelningar = ref<Array<any>>([]);

    const hamtaAvdelningar = async () => {
        try {
            const response = await klinikenApi.get(
                "/core/orgunits/",
                getConfig({ params: { vardenhetstyp: "slutenvardsavdelning" } })
            );
            avdelningar.value = response.data.results.map((avd: any) => ({
                value: avd.id,
                label: avd.alias,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta avdelningar.", "warning");
        }
    };

    const vardgivareOptions = ref([]);

    const hamtaVardgivare = async () => {
        try {
            const response = await klinikenApi.get("/core/externavardgivare/", getConfig());
            vardgivareOptions.value = response.data.results.map((vg: any) => ({
                value: vg.id,
                label: vg.alias,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta vårdgivare.", "warning");
        }
    };

    const vardpersoner = ref([]);

    const searchVardperson = async (search?: string) => {
        try {
            const response = await klinikenApi.get("/core/externavardpersoner/", getConfig({ params: { q: search } }));
            vardpersoner.value = response.data.results.map((operator: any) => ({
                value: operator.id,
                label: operator.namn,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta operatörer.", "warning");
        }
    };

    const getStatusFilter = () => {
        if (filterValues.value.status) {
            return filterValues.value.status;
        }

        return defaultStatus; // Fallback till standard status
    };

    const hamtaDagsoversikt = async () => {
        let params = {
            ...filterValues.value,
            status: getStatusFilter(),
            ordering: vardplatsOrdering,
        };

        try {
            const response = await klinikenApi.get("/slutenvard/slutenvardsbesok/", getConfig({ params }, tabName));
            const slutenvardsbesok: Slutenvardsbesok[] = response.data.results;

            count.value = response.data.count;

            items.value = slutenvardsbesok
                .map((besok: Slutenvardsbesok) => {
                    if (besok.anhorigbesok?.aktiv) {
                        count.value++;
                        return [besok, besok.anhorigbesok];
                    }
                    return besok;
                })
                .flat();
        } catch (error) {
            openDialog("Kunde inte hämta dagsöversikt", "warning");
        }
    };

    async function print() {
        if (!printablecontent.value) return;
        // Filtrerar bort anhörigbesök från items för att endast journallogga slutenvårdsbesök
        const printItems = items.value.filter((item): item is Slutenvardsbesok => "patient" in item);
        try {
            await printInIframe({
                url: "/slutenvard/slutenvardsbesok/utskrift/",
                items: printItems,
                logName: tabName,
                contentElement: printablecontent.value,
                orientation: "landscape",
                title: `Vårdplatsöversikt ${moment().format("YYYY-MM-DD HH:mm")}`,
            });
        } catch (_) {
            dialog.value?.show({
                variant: BasicModalVariant.ALERT,
                title: "Det gick inte att skriva ut.",
                okButton: "OK",
            });
        }
    }

    const getQuickFilters = (avdelningar: { value: string; label: string }[]) => {
        // Keep "Idag" filters and add "Inkommande imorgon" filters
        const todayFilters = avdelningar.map((avd) => ({
            key: `idag-${avd.value}`,
            text: `Idag ${avd.label}`,
            filters: {
                datum: today,
                sang__sal__vardavdelning: avd.value,
            },
        }));

        const incomingTomorrowFilters = avdelningar.map((avd) => ({
            key: `inkommande-imorgon-${avd.value}`,
            text: `Inkommande imorgon ${avd.label}`,
            filters: {
                indatum: tomorrow,
                status: VardplatsStatus.BOKAD,
                sang__sal__vardavdelning: avd.value,
            },
        }));

        return [...todayFilters, ...incomingTomorrowFilters];
    };

    // getSidaLabel, getOperationInfo, getInDatum moved to VardplatsdagsoversiktList.vue

    const getDayAndDate = (date: string) => {
        if (!date) {
            return "";
        }

        const dayInSwedishFormat = moment(date).locale("sv").format("dddd");

        return `${dayInSwedishFormat} ${date}`;
    };

    const getAvdelning = (avdelningId: string) => {
        if (!avdelningId || !avdelningar.value?.length) {
            return "";
        }

        const avdelning = avdelningar.value.find((a) => a.value === avdelningId).label;

        return avdelning;
    };

    const isFilterActive = (filter: any) => {
        const isTodayFilter = "datum" in filter.filters;

        if (isTodayFilter) {
            return (
                filterValues.value.datum === filter.filters.datum &&
                filterValues.value.sang__sal__vardavdelning === filter.filters.sang__sal__vardavdelning
            );
        }

        return (
            filterValues.value.indatum === filter.filters.indatum &&
            filterValues.value.status === filter.filters.status &&
            filterValues.value.sang__sal__vardavdelning === filter.filters.sang__sal__vardavdelning
        );
    };

    const applyQuickFilter = (filter: any) => {
        const isTodayFilter = "datum" in filter.filters;
        const quickFilters = {
            ...filterValues.value,
            ...filter.filters,
        };

        // Clear the other date type
        if (isTodayFilter) {
            quickFilters.indatum = undefined;
            quickFilters.status = undefined;
        } else {
            quickFilters.datum = undefined;
        }

        handleSearch(quickFilters);
    };

    onMounted(async () => {
        filterValues.value = {
            datum: today,
        };

        await hamtaAvdelningar();
        await hamtaVardgivare();
        await searchVardperson();
        await hamtaDagsoversikt();
    });
</script>

<template>
    <div class="vardplatsdagsoversikt">
        <div class="header">
            <h1>Dagsöversikt</h1>
        </div>

        <SearchFilter id="search-filter" v-model="filterValues" :fields="filterFields" @input="handleSearch">
            <template #quick-filters>
                <a
                    v-for="filter in getQuickFilters(avdelningar)"
                    :key="filter.key"
                    class="quick-filter-link"
                    :class="{ active: isFilterActive(filter) }"
                    @click="applyQuickFilter(filter)"
                >
                    {{ filter.text }}
                </a>
            </template>
            <template #fields>
                <FormulateInput type="datepicker" label="Datum" name="datum" />
                <FormulateInput type="datepicker" label="Inkommande datum" name="indatum" />
                <FormulateInput
                    type="vueselect"
                    label="Avdelning"
                    name="sang__sal__vardavdelning"
                    :options="avdelningar"
                />
                <FormulateInput
                    type="vueselect"
                    label="Vårdgivare"
                    name="vardplatsbestallning__vardgivare"
                    :options="vardgivareOptions"
                />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__operator"
                    label="Operatör"
                    :options="vardpersoner"
                    @search="searchVardperson"
                    class="autocomplete"
                />

                <FormulateInput type="vueselect" label="Status" name="status" :options="STATUSAR" />
            </template>
        </SearchFilter>

        <KButton class="print" size="secondary" @click="print()" :disabled="items.length === 0">Skriv ut</KButton>

        <div class="subheader">
            <span>
                Visar <strong>{{ items.length }}</strong> av <strong>{{ count }}</strong> träffar
            </span>
        </div>

        <hr />

        <div ref="printablecontent">
            <div class="table-caption">
                <strong>Dagsöversikt</strong>
                <strong>{{ getDayAndDate(filterValues.datum || filterValues.indatum) }}</strong>
                <strong>{{ getAvdelning(filterValues.sang__sal__vardavdelning) }}</strong>
            </div>

            <VardplatsdagsoversiktList :items="items" />
        </div>
        <ModalBasic ref="dialog" />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";
    @import "@/style/form";

    .vardplatsdagsoversikt {
        .subheader {
            display: flex;
            justify-content: space-between;
            margin: 20px 0px;

            span {
                font-size: 22px;
                line-height: 30px;
                font-weight: normal;
            }

            strong {
                font-size: 22px;
                line-height: 30px;
                font-weight: normal;
                font-family:
                    Roboto Medium,
                    sans-serif;
            }
        }

        .table-caption {
            display: flex;
            column-gap: 21px;
            margin-bottom: 12px;

            strong {
                color: $color-table-header;
                font-size: $font-size-small;
                line-height: $line-height-small;
                text-transform: capitalize;
            }
        }
    }
</style>
